import React, { useEffect } from 'react';
import { Scheme } from '../../types/client';
import {
  isSchemeSelectable,
  getStrategyOptions,
  getSchemeIdsForStrategy,
} from '../../utils/orderSchemeGroup';
import {
  FormField,
  FormReactSelect,
  FormReactMultiSelect,
  SelectOption,
} from '../utils/FormCard';

interface OrderSchemeSelectorProps {
  schemes: Scheme[];
  /** Currently selected scheme ids. */
  value: number[];
  /**
   * Called with the new scheme ids from either field. The parent owns the
   * knock-on form state (volume cap, cash buffer, client list) because those
   * rules differ between the buy and sell forms.
   */
  onChange: (schemeIds: number[]) => void;
  error?: string;
  disabled?: boolean;
}

/**
 * The Investment Strategy + Schemes pair shared by the buy and sell order forms.
 *
 * Picking a strategy replaces the scheme selection with that strategy's schemes;
 * individual schemes can then be removed. The strategy is a selection shortcut
 * only — it is never submitted.
 */
const OrderSchemeSelector: React.FC<OrderSchemeSelectorProps> = ({
  schemes,
  value,
  onChange,
  error,
  disabled = false,
}) => {
  const [strategyId, setStrategyId] = React.useState<number | null>(null);
  // Schemes the last strategy pick could not take because of the order calc type,
  // base currency or region rule. Reported so a short selection is never a surprise.
  const [omittedCount, setOmittedCount] = React.useState(0);

  // A strategy label with nothing selected under it is misleading, so drop it
  // once the schemes are gone — whether cleared here or reset by the parent.
  useEffect(() => {
    if (value.length === 0) {
      setStrategyId(null);
      setOmittedCount(0);
    }
  }, [value.length]);

  const strategyOptions: SelectOption[] = getStrategyOptions(schemes).map(s => ({
    value: s.id,
    label: s.name,
  }));
  const selectedStrategy = strategyOptions.find(o => o.value === strategyId) ?? null;

  const schemeOptions: SelectOption[] = schemes.map(s => ({
    value: s.id,
    label: s.code ?? String(s.id),
  }));
  const selectedSchemes = schemeOptions.filter(o => value.includes(o.value));

  return (
    <>
      <FormField label="Investment Strategy">
        <FormReactSelect
          options={strategyOptions}
          value={selectedStrategy}
          onChange={(selected) => {
            const id = selected ? selected.value : null;
            setStrategyId(id);
            if (id === null) {
              setOmittedCount(0);
              onChange([]);
              return;
            }
            const { schemeIds, omittedCount: omitted } = getSchemeIdsForStrategy(id, schemes);
            setOmittedCount(omitted);
            onChange(schemeIds);
          }}
          placeholder="Select strategy…"
          isDisabled={disabled}
        />
      </FormField>

      <FormField
        label="Schemes"
        required
        error={error}
        hint={omittedCount > 0
          ? omittedCount === 1
            ? '1 other scheme in this strategy uses a different order calculation type, '
              + 'base currency or region, so it was left out — one order cannot mix them.'
            : `${omittedCount} other schemes in this strategy use a different order `
              + 'calculation type, base currency or region, so they were left out — one '
              + 'order cannot mix them.'
          : undefined}
      >
        <FormReactMultiSelect
          options={schemeOptions}
          value={selectedSchemes}
          onChange={(selected) => {
            // A hand-picked change makes the strategy's omission note stale.
            setOmittedCount(0);
            onChange(selected ? selected.map(o => o.value) : []);
          }}
          placeholder="Select schemes…"
          hasError={Boolean(error)}
          isDisabled={disabled}
          isOptionDisabled={(option) => {
            // An order cannot mix order calculation types or base currencies.
            const scheme = schemes.find(s => s.id === option.value);
            return scheme ? !isSchemeSelectable(scheme, value, schemes) : false;
          }}
        />
      </FormField>
    </>
  );
};

export default OrderSchemeSelector;
